import React, {useContext, useEffect, useRef, useState} from "react"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import {TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage, injectIntl} from "react-intl";
import {AdtfDeviceOverviewContext} from "./context/AdtfDeviceOverviewContext";
import {
    chainWalk,
    getAssetRefreshInterval,
    sortComparator,
    getTestConfigKey, convertADTFSorterConfigIntoDBFormat, flattenADTFSorterConfig,
} from "../../util/Util";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import AddAdtfSorterConfigurationModal from "./modal/AddAdtfSorterConfigurationModal";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import Loader from "../../loader/Loader";
import DeleteAdtfSorterConfigurationModal from "./modal/DeleteAdtfSorterConfigurationModal";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import AddAdtfSorterTestPackageModal from "./modal/AddAdtfSorterTestPackageModal";
import DeleteAdtfSorterTestPackageModal from "./modal/DeleteAdtfSorterTestPackageModal";
import UploadFileModal from "../../commonui/UploadFileModal";
import {ADTFSorterConfigCSVParser, StackedStowTestConfigCSVParser, TestConfigCSVParser} from "../../util/Parsers";
import {isStackedStowTestApplicable} from "../../util/Validations";
import {
    ADTF_ASSET_TYPE, ASCENDING, ASSET_CONFIG_ATTRIBUTES, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR
} from "../../util/Constants";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import Text from "@amzn/meridian/text";
import {getAssetVersions, getAssetByVersion} from "../../resources/AssetResource";
import RollbackModal from "../../rollback/RollbackModal";
import Input from "@amzn/meridian/input";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import {getLocalizedMessage} from "../../util/Localization";
import {ConditionalWrapping, tooltipWrapper} from "../../util/ConditionalWrapping";

const ADTF_CONFIG_ATTRIBUTES = ASSET_CONFIG_ATTRIBUTES[ADTF_ASSET_TYPE];

const AdtfDeviceConfiguration = ({intl}) => {

    const {viewModel, dispatch} = useContext(AdtfDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openCSVModal, setOpenCSVModal] = useState(false)
    const [valueConfiguration, setValueConfiguration] = useState("")
    const [selectedMac, setSelectedMac] = useState("")
    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [sorterTestPackageSortColumn, setSorterTestPackageSortColumn] = useState("scannables");
    const [sorterTestPackageSortOrder, setSorterTestPackageSortOrder] = useState(ASCENDING);
    const [sorterDestinationSortColumn, setSorterDestinationSortColumn] = useState("destinationId");
    const [sorterDestinationSortOrder, setSorterDestinationSortOrder] = useState(ASCENDING);
    const tableEndRef = useRef(null);
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [sortZoneSearchInput, setSortZoneSearchInput] = useState('');
    const [bagSearchInput, setBagSearchInput] = useState('');
    const { showCustomAlert } = useContext(GlobalStateContext);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE, CONFIG_TYPE.ADTF_SORTER_DESTINATION]

    // Single place deciding whether the stacked stow mock test fields exist for this ADTF.
    const isStackedStowTestEnabled = isStackedStowTestApplicable(
        getDraftData({stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION, subKey: selectedMac}));

    const stackedStowTestColumns = isStackedStowTestEnabled
        ? [
            {
                key: "bag", labelId: "bay.bag", defaultLabel: "Bag",
                searchValue: bagSearchInput, onSearchChange: setBagSearchInput
            },
            {key: "tier", labelId: "bay.tier", defaultLabel: "Tier"}
        ]
        : [];

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
            subKey: selectedMac
        })

        data = data.filter(item => getTestConfigKey(item.scannables)
                .toLowerCase()
                .includes(scannablesSearchInput.toLowerCase()))
            .filter(item => item.sortZone
                .toLowerCase()
                .includes(sortZoneSearchInput.toLowerCase())
            )
            // Bag is optional, so a row without one is matched by an empty search and filtered
            // out by any other term
            .filter(item => (item.bag || '')
                .toLowerCase()
                .includes(bagSearchInput.toLowerCase())
            )
        data = data.sort((a, b)=>
            sortTestConfigColumns[sorterTestPackageSortColumn](a, b, sorterTestPackageSortOrder === ASCENDING)
        )
        return data;
    }

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(versionsData.data.assetVersionDetailsList);
        setIsVersionsLoading(false);
    }

    const loadRollbackDataToDraft = async () => {
        let rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const sortTestPackageData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[ADTF_CONFIG_ATTRIBUTES.SORTER_TEST_PACKAGE]), []);
        // Stored as a sorterId -> configs map, but the draft and the table below hold the flattened rows,
        // so this has to be flattened on the way in - a bare map has no length and loaded nothing.
        const sorterConfigData = flattenADTFSorterConfig(
            chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[ADTF_CONFIG_ATTRIBUTES.SORTER_CONFIG]), []));
        if(sortTestPackageData.length) {
            setDraftData({
                data: sortTestPackageData,
                stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
        if(sorterConfigData.length) {
            setDraftData({
                data: sorterConfigData,
                stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal]);

    useEffect(() => {
        setDraftData({
           data: chainWalk(() => viewModel.sorterTestPackageConfig[selectedMac], []),
           stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
           subKey: selectedMac,
        })
        setDraftData({
            data: chainWalk(() => viewModel.sorterConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getSortedDeviceList = () => {
        const sortedDeviceList = Object.values(viewModel.adtfDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const getData = () => {
        if(isDataEdited(PAGE_CONFIGURATION_KEYS)) return;
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const updateDestinationConfigDraftData = (sorterConfig) => {
        setDraftData({
            data: chainWalk(() => sorterConfig, []),
            stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const updateTestConfigDraftData = (sorterTestPackage) => {
        setDraftData({
            data: chainWalk(() => sorterTestPackage, []),
            stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const deployDraftADTFAsset = () => {
        const sorterDestinationList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
            subKey: selectedMac
        })
        const testPackageList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
            subKey: selectedMac
        })

        dispatch({
            type: 'UPDATE_ADTF_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                showCustomAlert: showCustomAlert,
                configurations: {
                    sorterTestPackage: testPackageList,
                    sorterConfig: convertADTFSorterConfigIntoDBFormat(sorterDestinationList)
                },
                mac: selectedMac
            }
        });

        setDraftData({
            data: sorterDestinationList,
            stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
            subKey: selectedMac,
            override: true
        })
        setDraftData({
            data: testPackageList,
            stateKey: CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE,
            subKey: selectedMac,
            override: true
        })
    }

    function onClickUploadCSV() {
        setOpenCSVModal(true)
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    const getDataStringFromConfig = (config) => {
        return config.installation.locations.join(' ');
    }

    const renderSorterConfigTableRow = (config, index) => {
        const dataString = getDataStringFromConfig(config)
        const prevDataString = chainWalk(() => getDataStringFromConfig(config.prev), null)
        return (
            <TableRow highlightOnHover={true} key={index} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell alignmentVertical="top">
                    <Column spacingInset="200 none none">{config.destinationId}</Column>
                </TableCell>
                <TableCell>
                    {config.sorterId}
                </TableCell>
                <TableCell>
                    {config.installation.type}
                </TableCell>
                <TableCell>
                    <Row>
                        {prevDataString && <Text> {prevDataString} </Text>}
                        {prevDataString && <Icon tokens={arrowRightTokens}/>}
                        <Text> {dataString} </Text>
                    </Row>
                </TableCell>
            </TableRow>
        )
    }

    const sortDestinationConfigColumns = {
        destinationId: (a, b, ascending) => {
            return ascending ? sortComparator(a.destinationId, b.destinationId) : -sortComparator(a.destinationId, b.destinationId);
        },
        sorterId: (a, b, ascending) => {
            return ascending ? sortComparator(a.sorterId, b.sorterId) : -sortComparator(a.sorterId, b.sorterId);
        },
        installationType: (a, b, ascending) => {
            return ascending ? sortComparator(a.installation.type, b.installation.type) : -sortComparator(a.installation.type, b.installation.type);
        },
        location: (a, b, ascending) => {
            return ascending ? sortComparator(getDataStringFromConfig(a), getDataStringFromConfig(b)) : -sortComparator(getDataStringFromConfig(a), getDataStringFromConfig(b));
        }
    }

    const sortTestConfigColumns = {
        scannables: (a, b, ascending) => {
            let scannableFirst = Array.isArray(a.scannables) ? a.scannables.join(' ') : a.scannables;
            let scannableSecond = Array.isArray(b.scannables) ? b.scannables.join(' ') : b.scannables;
            return ascending ? sortComparator(scannableFirst, scannableSecond) : -sortComparator(scannableFirst, scannableSecond);
        },
        sortZone: (a, b, ascending) => {
            return ascending ? sortComparator(a.sortZone, b.sortZone) : -sortComparator(a.sortZone, b.sortZone);
        },
        bag: (a, b, ascending) => {
            return ascending ? sortComparator(a.bag, b.bag) : -sortComparator(a.bag, b.bag);
        },
        tier: (a, b, ascending) => {
            return ascending ? sortComparator(a.tier, b.tier) : -sortComparator(a.tier, b.tier);
        }
    }

    const renderSorterConfigTableHeader = () => {
        return (<TableRow highlightOnHover={false}>
            <TableCell sortColumn="destinationId">
                Destination Id
            </TableCell>
            <TableCell sortColumn="sorterId">
                Sorter Id
            </TableCell>
            <TableCell sortColumn="installationType">
                Installation Type
            </TableCell>
            <TableCell sortColumn="location">
                Locations
            </TableCell>
        </TableRow>)
    }

    const getHeaderProps = () => {
        return {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            isSorterConfig: valueConfiguration === CONFIG_TYPE.ADTF_SORTER_DESTINATION, isAuthorized, intl,
            defaultSelection: "",
            resetDraftData,
            deployDraft: deployDraftADTFAsset,
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp],
            isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
            isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
            rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
        }
    }

    /**
     * Renders one field of a test package row, showing "previous -> new" when a draft edit has
     * changed it. Shared by every field of the row so the columns stay visually consistent.
     */
    const renderDiffCell = (config, field) => {
        const dataString = config[field]
        const prevDataString = chainWalk(() => config.prev[field], null)
        const isChanged = prevDataString && prevDataString !== dataString;
        return (
            <React.Fragment>
                {isChanged && <Text> {prevDataString} </Text>}
                {isChanged && <Icon tokens={arrowRightTokens}/>}
                <Text> {dataString} </Text>
            </React.Fragment>
        )
    }

    const renderTestConfigTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    <FormattedMessage id="bay.index" defaultMessage="Index"/>
                </TableCell>
                <TableCell sortColumn="scannables">
                    <FormattedMessage id="bay.scannables" defaultMessage="Scannables"/>
                </TableCell>
                <TableCell sortColumn="sortZone">
                    <FormattedMessage id="bay.sortzone" defaultMessage="Sort Zone"/>
                </TableCell>
                {stackedStowTestColumns.map(({key, labelId, defaultLabel}) =>
                    <TableCell key={key} sortColumn={key}>
                        <FormattedMessage id={labelId} defaultMessage={defaultLabel}/>
                    </TableCell>
                )}
            </TableRow>
        )
    }

    const renderTestConfigTableRow = (config, serialNumber) => {
        return  (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {serialNumber}
                </TableCell>
                <TableCell>
                    {Array.isArray(config.scannables) ? config.scannables.join(' ') : config.scannables}
                </TableCell>
                <TableCell>
                    {renderDiffCell(config, "sortZone")}
                </TableCell>
                {stackedStowTestColumns.map(({key}) =>
                    <TableCell key={key}>
                        {renderDiffCell(config, key)}
                    </TableCell>
                )}
            </TableRow>
        )
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={sortZoneSearchInput}
                        onChange={setSortZoneSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                {stackedStowTestColumns.map(({key, searchValue, onSearchChange}) =>
                    <TableCell key={key}>
                        {onSearchChange &&
                        <Input
                            className="input-search-class"
                            value={searchValue}
                            onChange={onSearchChange}
                            type="text"
                            placeholder="Search"
                            width="70%"
                            size="small"/>}
                    </TableCell>
                )}
                <TableCell/>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
                {valueConfiguration === CONFIG_TYPE.ADTF_SORTER_DESTINATION && selectedMac &&
                <Column>
                    <PaginatedTable
                        data={getDraftData({stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION, subKey: selectedMac}).sort((a, b) => {
                            return sortDestinationConfigColumns[sorterDestinationSortColumn](a, b, sorterDestinationSortOrder === ASCENDING);
                        })}
                        renderRow={renderSorterConfigTableRow}
                        renderHeader={renderSorterConfigTableHeader}
                        sortColumn={sorterDestinationSortColumn}
                        sortDirection={sorterDestinationSortOrder}
                        onSort={({sortColumn, sortDirection}) => {
                            setSorterDestinationSortColumn(sortColumn);
                            setSorterDestinationSortOrder(sortDirection);
                        }}
                    />
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickAddSingle} type="primary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="sorterconfig.addupdate"
                                                  defaultMessage="Add/Update Sorter Destination Configuration"/>
                            </Button>}
                        />
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickUploadCSV} type="primary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={uploadTokens}/>
                                <FormattedMessage id="sorterconfig.uploadCSV"
                                                  defaultMessage="Upload Sorter Destination Configuration CSV"/>
                            </Button>}
                        />
                        <Box></Box>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickDeleteConfiguration} size="small" type="tertiary"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={trashTokens}/>
                                <FormattedMessage id="config.delete" defaultMessage="Delete Configuration"/>
                            </Button>}
                        />
                    </Row>
                    <UploadFileModal mac={selectedMac}
                                     open={openCSVModal} setOpen={setOpenCSVModal}
                                     parser={ADTFSorterConfigCSVParser} updateConfig={updateDestinationConfigDraftData}/>
                    <AddAdtfSorterConfigurationModal mac={selectedMac}
                                                     open={openAddModal} setOpen={setOpenAddModal}/>
                    <DeleteAdtfSorterConfigurationModal mac={selectedMac}
                                                        open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                    <div ref={tableEndRef}/>
                </Column>}
                {valueConfiguration === CONFIG_TYPE.ADTF_SORTER_TEST_PACKAGE && selectedMac &&
                <Column>
                    <PaginatedTable
                        data={searchDataForTestConfigTable()}
                        renderRow={renderTestConfigTableRow}
                        renderHeader={renderTestConfigTableHeader}
                        renderSearchRow={renderSearchRow}
                        sortColumn={sorterTestPackageSortColumn}
                        sortDirection={sorterTestPackageSortOrder}
                        onSort={({sortColumn, sortDirection}) => {
                            setSorterTestPackageSortColumn(sortColumn);
                            setSorterTestPackageSortOrder(sortDirection);
                        }}
                    />
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <Button onClick={onClickAddSingle}
                                type="primary"
                                size="small">
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="sortertestconfig.addupdatetest"
                                              defaultMessage="Add/Update Sorter Test Package Configuration"/>
                        </Button>
                        <Button onClick={onClickUploadCSV}
                                type="primary"
                                size="small">
                            <Icon tokens={uploadTokens}/>
                            <FormattedMessage id="sortertestconfig.uploadCSV"
                                              defaultMessage="Upload Sorter Test Package Configuration CSV"/>
                        </Button>
                        <Box></Box>
                        <Button onClick={onClickDeleteConfiguration}
                                size="small" type="tertiary">
                            <Icon tokens={trashTokens}/>
                            <FormattedMessage id="config.deletetest" defaultMessage="Delete Sorter Test Package Configuration"/>
                        </Button>
                    </Row>
                    <UploadFileModal mac={selectedMac}
                                     open={openCSVModal} setOpen={setOpenCSVModal}
                                     parser={isStackedStowTestEnabled ? StackedStowTestConfigCSVParser : TestConfigCSVParser}
                                     updateConfig={updateTestConfigDraftData}/>
                    <AddAdtfSorterTestPackageModal mac={selectedMac}
                                                   open={openAddModal} setOpen={setOpenAddModal}/>
                    <DeleteAdtfSorterTestPackageModal mac={selectedMac}
                                                      open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                    <div ref={tableEndRef}/>
                </Column>}
            </Column>
        );
    }
}

export default injectIntl(AdtfDeviceConfiguration)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtfdevice/AdtfDeviceConfiguration.js